<template>
  <div class="length-measurement-viz">
    
    <div class="viz-header" >
      <div class="brief" v-latex="knowledgePoint"></div>
      <div class="formula" v-latex>$<strong>读数 = 准确值 + 估读值 | 单位：mm/cm</strong>$</div>
    </div>

    <div class="main-layout vertical">
      <!-- 仿真区域 -->
      <div class="simulation-stage dark-stage">
        <!-- 长度模式 -->
        <svg v-if="mode === 'length'" width="100%" height="320" viewBox="0 0 500 320" preserveAspectRatio="xMidYMid meet">
          <!-- 背景桌面 -->
          <rect x="0" y="240" width="500" height="80" fill="#1e293b" opacity="0.3" />
          
          <!-- 被测物体 (木块) -->
          <rect :x="objectX" y="200" :width="objectWidth * scale" height="40" fill="#f59e0b" stroke="#d97706" stroke-width="2" rx="4" />
          <!-- <text :x="objectX + objectWidth*scale/2" y="225" fill="#fef3c7" font-size="12" text-anchor="middle" font-weight="bold">被测对象</text> -->

          <!-- 刻度尺 -->
          <g :transform="`translate(${rulerX}, 140)`">
             <!-- 尺身 -->
             <rect x="0" y="0" width="400" height="60" fill="rgba(255,255,255,0.9)" stroke="#94a3b8" stroke-width="2" rx="4" />
             
             <!-- 刻度线 (每 1cm = 40px, 每 1mm = 4px) -->
             <g v-for="i in 101" :key="'mm'+i">
                <line 
                  :x1="(i-1) * 4" 
                  y1="0" 
                  :x2="(i-1) * 4" 
                  :y2="(i-1) % 10 === 0 ? 25 : ((i-1) % 5 === 0 ? 18 : 10)" 
                  stroke="#334155" 
                  :stroke-width="(i-1) % 10 === 0 ? 1.5 : 0.8" 
                />
                <text v-if="(i-1) % 10 === 0" :x="(i-1) * 4" y="45" fill="#1e293b" font-size="12" text-anchor="middle" font-weight="900" font-family="monospace">
                  {{ (i-1) / 10 }}
                </text>
             </g>
             <!-- <text x="380" y="52" fill="#64748b" font-size="10" font-weight="bold">cm</text> -->
          </g>

          <!-- 读数指示器 (放大镜效果) -->
          <g v-if="showZoom" :transform="`translate(${zoomPosX - 50}, 20)`">
             <circle cx="50" cy="50" r="48" fill="#fff" stroke="#3b82f6" stroke-width="3" />
             <clipPath id="zoomClip">
                <circle cx="50" cy="50" r="48" />
             </clipPath>
             
             <g clip-path="url(#zoomClip)">
                <!-- 放大后的刻度轴 -->
                <g :transform="`translate(${(rulerX - zoomPosX)*4 + 50}, 50) scale(4)`">
                    <!-- 再次绘制刻度以保持高清晰度 -->
                    <rect x="0" y="-30" width="400" height="60" fill="#fff" />
                    <g v-for="i in 101" :key="'zmm'+i">
                        <line :x1="(i-1) * 4" y1="-30" :x2="(i-1) * 4" :y2="(i-1) % 10 === 0 ? -10 : -22" stroke="#000" stroke-width="0.5" />
                    </g>
                    <!-- 被测物边缘 -->
                    <rect :x="objectWidth*scale" y="-30" width="5" height="60" fill="rgba(245, 158, 11, 0.4)" />
                    <line :x1="objectWidth*scale" y1="-30" :x2="objectWidth*scale" y2="30" stroke="#f59e0b" stroke-width="1" />
                </g>
             </g>
             <!-- 中心测量线 -->
             <line x1="50" y1="2" x2="50" y2="98" stroke="#ef4444" stroke-width="1.5" stroke-dasharray="4,2" />
             <!-- <text x="50" y="115" fill="#3b82f6" font-size="11" text-anchor="middle" font-weight="bold">末端对齐细节</text> -->
          </g>
          
          <!-- <text x="20" y="300" fill="rgba(255,255,255,0.4)" font-size="10" font-weight="bold">初中物理·长度测量实验室</text> -->
        </svg>

        <!-- 2. 游标卡尺模式 -->
        <svg v-else-if="mode === 'vernier'" width="100%" height="320" viewBox="0 0 500 320" preserveAspectRatio="xMidYMid meet">
           <!-- 背景物体 -->
           <rect x="25" y="100" :width="objectWidth * scale" height="120" fill="rgba(245, 158, 11, 0.2)" stroke="#f59e0b" stroke-dasharray="4,2" />
           <!-- 主尺 (固定) -->
           <g transform="translate(0, 50)">
              <path d="M 0 0 L 500 0 L 500 50 L 50 50 L 50 150 L 25 150 L 25 50 L 0 50 Z" fill="#cbd5e1" stroke="#475569" />
              <g v-for="i in 50" :key="'m'+i">
                 <line :x1="25 + (i-1)*10" y1="0" :x2="25 + (i-1)*10" :y2="(i-1)%10===0?18:10" stroke="#000" />
                 <text v-if="(i-1)%10===0" :x="25+(i-1)*10" y="32" font-size="10" text-anchor="middle" font-weight="900">{{ (i-1)/10 }}</text>
              </g>
           </g>
           <!-- 游标尺 (可动) -->
           <g :transform="`translate(${objectWidth * scale}, 50)`">
              <path d="M 25 0 L 150 0 L 150 120 L 50 120 L 25 210 L 0 210 L 25 120 L 0 120 L 0 0 Z" fill="#94a3b8" stroke="#334155" opacity="0.95" />
              <!-- 游标刻度 (0.1mm 分度: 10格对应主轴 9mm) -->
              <g v-for="i in 11" :key="'v'+i">
                 <line :x1="25 + (i-1)*9" y1="120" :x2="25 + (i-1)*9" :y2="105" stroke="#000" stroke-width="1.2" />
                 <text v-if="(i-1)%5===0" :x="25+(i-1)*9" y="100" font-size="9" text-anchor="middle" font-weight="bold">{{ (i-1) }}</text>
              </g>
              <line x1="25" y1="0" x2="25" y2="210" stroke="rgba(239, 68, 68, 0.4)" stroke-dasharray="2,2" />
           </g>
        </svg>

        <!-- 3. 停表模式 -->
        <svg v-else width="100%" height="320" viewBox="0 0 500 320" preserveAspectRatio="xMidYMid meet">
           <g transform="translate(250, 160)">
              <circle r="120" fill="#334155" stroke="#94a3b8" stroke-width="6" />
              <circle r="110" fill="#fff" />
              <!-- 大表盘 (秒: 0-30s/圈) -->
              <g v-for="i in 60" :key="'s'+i" :transform="`rotate(${(i-1)*6})`">
                 <line x1="0" y1="-110" x2="0" :y2="(i-1) % 10 === 0 ? -95 : -102" stroke="#000" stroke-width="1" />
                 <text v-if="(i-1) % 10 === 0" x="0" y="-82" fill="#000" font-size="12" text-anchor="middle" font-weight="900" :transform="`rotate(${-(i-1)*6})`">{{ (i-1)/2 }}</text>
              </g>
              <!-- 小表盘 (分: 0-15min) -->
              <g transform="translate(0, -45)">
                 <circle r="35" fill="none" stroke="#64748b" stroke-width="2" />
                 <g v-for="i in 16" :key="'m'+i" :transform="`rotate(${(i-1)*24})`">
                    <line x1="0" y1="-35" x2="0" y2="-30" stroke="#000" stroke-width="0.5" />
                 </g>
                 <line x1="0" y1="0" x2="0" y2="-28" stroke="#ef4444" stroke-width="3" stroke-linecap="round" :transform="`rotate(${minutes * 24})`" />
              </g>
              <!-- 大秒针 -->
              <line x1="0" y1="0" x2="0" y2="-100" stroke="#ef4444" stroke-width="2.5" stroke-linecap="round" :transform="`rotate(${seconds * 12})`" />
              <circle r="5" fill="#1e293b" />
           </g>
        </svg>

        <div class="hud-top">
           <div class="stat-box" v-if="mode === 'length'">
              <label>分度值</label>
              <span>1 mm (0.1 cm)</span>
           </div>
           <div class="stat-box" v-else-if="mode === 'vernier'">
              <label>卡尺精度</label>
              <span>0.1 mm</span>
           </div>
           <div class="stat-box" v-else>
              <label>累计时间</label>
              <span>{{ (minutes + seconds/60).toFixed(2) }} min</span>
           </div>
           <div class="stat-box">
              <label>{{ mode === 'time' ? '秒表读数' : '当前值' }}</label>
              <span>
                {{ mode === 'length' ? (objectWidth / 10).toFixed(2) + ' cm' : (mode === 'vernier' ? (objectWidth/10).toFixed(1) + ' cm' : (Math.floor(minutes) + ':' + (seconds < 10 ? '0' : '') + seconds.toFixed(1))) }}
              </span>
           </div>
        </div>

        <div class="estimation-tip" v-if="isAlignedCorrectly && mode === 'length'">
           <i class="el-icon-info"></i>
           <span><strong>小贴士：</strong>读数需估读到分度值的下一位。当前刻度落于 <strong>{{ floorVal }}</strong> 与 <strong>{{ ceilVal }}</strong> 之间。</span>
        </div>
      </div>

      <!-- 控制面板 -->
      <div class="bottom-controls">
         <div class="control-card glass">
            <div class="mode-switch">
               <el-radio-group v-model="mode" size="mini">
                  <el-radio-button label="length">刻度尺</el-radio-button>
                  <el-radio-button label="vernier">游标卡尺</el-radio-button>
                  <el-radio-button label="time">停表</el-radio-button>
               </el-radio-group>
            </div>
            <template v-if="mode === 'length' || mode === 'vernier'">
               <div class="control-row" v-if="mode === 'length'">
                  <label>移动刻度尺</label>
                  <el-slider v-model="rulerX" :min="0" :max="150" size="small"></el-slider>
               </div>
               <div class="control-row">
                  <label>调节物体尺寸 ({{ (objectWidth/10).toFixed(2) }} cm)</label>
                  <el-slider v-model="objectWidth" :min="10" :max="45" :step="0.1" size="small"></el-slider>
               </div>
               <div class="control-row checkbox-row" v-if="mode === 'length'">
                  <el-checkbox v-model="showZoom">开启高清放大镜 (观察末端估读)</el-checkbox>
               </div>
            </template>
            <template v-else>
               <div class="control-row">
                  <label>秒表模拟</label>
                  <el-slider v-model="totalSeconds" :min="0" :max="900" :step="0.1" size="small"></el-slider>
               </div>
            </template>
         </div>

         <div class="discovery-card-compact" v-latex>
            <p v-if="mode === 'length'"><strong>刻度尺：</strong>读数需估读到分度值的下一位。</p>
            <p v-else-if="mode === 'vernier'"><strong>游标卡尺：</strong>找出一根与主尺刻度线对得最齐的游标线，不需估读。</p>
            <p v-else><strong>停表读数：</strong>遵循“小盘看分，大盘看秒”的联动原则。</p>
         </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'LengthMeasurementViz',
  props: { knowledgePoint: { type: String, default: '' } },
  data() {
    return {
      mode: 'length',
      rulerX: 20,
      objectX: 100, // For ruler mode
      objectWidth: 34.5, // mm
      scale: 4, // 1mm = 4px for ruler, 1mm = 10px for vernier
      showZoom: true,
      totalSeconds: 45.2
    }
  },
  watch: {
    knowledgePoint: {
       handler() { this.detectMode(); },
       immediate: true
    },
    mode(val) {
       this.scale = (val === 'vernier') ? 10 : 4;
       this.objectX = (val === 'vernier') ? 25 : 100; // Adjust objectX based on mode
       this.objectWidth = (val === 'vernier') ? 25.5 : 34.5; // Reset object width for better initial view
       this.rulerX = 20; // Reset ruler position
    }
  },
  computed: {
    minutes() { return Math.floor(this.totalSeconds / 60); },
    seconds() { return this.totalSeconds % 60; },
    isAlignedCorrectly() {
       // Check if ruler 0 is roughly aligned with object start for length mode
       return this.mode === 'vernier' || Math.abs(this.rulerX - this.objectX) < 10;
    },
    zoomPosX() {
       return this.objectX + this.objectWidth * this.scale;
    },
    floorVal() {
       return (Math.floor(this.objectWidth) / 10).toFixed(1);
    },
    ceilVal() {
       return (Math.ceil(this.objectWidth) / 10).toFixed(1);
    }
  },
  methods: {
    detectMode() {
       const kp = (this.knowledgePoint || '').toString();
       if (kp.includes('时')) this.mode = 'time';
       else if (kp.includes('卡尺') || kp.includes('游标')) this.mode = 'vernier';
       else this.mode = 'length';
    }
  }
}
</script>

<style scoped lang="scss">
.length-measurement-viz { flex: 1; display: flex; flex-direction: column; gap: 8px; height: 100%; overflow: hidden; }
.viz-header { display: flex; justify-content: space-between; align-items: center; padding: 0 4px; .brief { font-size: 14px; font-weight: bold; } .formula { font-size: 12px; color: #3b82f6; } }
.main-layout { flex: 1; display: flex; flex-direction: column; }
.simulation-stage { background: #020617; border-radius: 12px; position: relative; height: 320px; overflow: hidden; }
.hud-top { position: absolute; top: 12px; left: 12px; display: flex; gap: 10px; }
.stat-box { background: rgba(15, 23, 42, 0.85); border-left: 3px solid #3b82f6; padding: 4px 10px; border-radius: 6px; label { font-size: 10px; color: #94a3b8; display: block; } span { font-size: 13px; color: #fff; font-weight: 900; } }
.estimation-tip {
  position: absolute; bottom: 15px; right: 15px; left: 15px; background: rgba(59, 130, 246, 0.1); border: 1px solid rgba(59, 130, 246, 0.2); padding: 10px 15px; border-radius: 8px; color: #93c5fd; font-size: 12px; display: flex; align-items: center; gap: 10px;
  i { font-size: 16px; }
}
.bottom-controls { flex: 1; overflow-y: auto; padding: 10px 0; }
.control-card { background: #fff; padding: 12px 18px; border-radius: 10px; border: 1px solid #e2e8f0; }
.mode-switch { 
  margin-bottom: 12px; 
  text-align: center; 
  .el-radio-group {
    display: flex;
    width: 100%;
    .el-radio-button {
      flex: 1;
      ::v-deep .el-radio-button__inner {
        width: 100%;
        padding: 8px 4px;
        white-space: nowrap;
      }
    }
  }
}
.control-row { margin-bottom: 8px; label { font-size: 11px; color: #64748b; font-weight: 900; } }
.checkbox-row { margin-bottom: 0; }
.discovery-card-compact { background: #f0fdf4; border: 1px solid #bbf7d0; padding: 10px 15px; border-radius: 10px; font-size: 12px; color: #166534; line-height: 1.5; }
</style>
